export const CHECKLIST_PENDING_STEP_STORAGE_KEY = 'onboarding_checklist_pending_step';

/** DOM event + postMessage type when checklist step status changes. */
export const CHECKLIST_PROGRESS_EVENT = 'onboarding-checklist-progress-updated';

/** Same-origin BroadcastChannel for parent app ↔ checklist iframe. */
export const CHECKLIST_PROGRESS_BROADCAST_CHANNEL = 'onboarding-checklist-progress';

const getChecklistStateStorageKey = (companyId) => `onboarding_checklist_state_${companyId}`;

/** Suppress storage-driven checklist refetch briefly after we write localStorage ourselves. */
let checklistStorageWriteSuppressUntil = 0;
const CHECKLIST_STORAGE_SUPPRESS_MS = 200;

let checklistNotifyTimer = null;

export const markChecklistStorageWrite = () => {
    checklistStorageWriteSuppressUntil = Date.now() + CHECKLIST_STORAGE_SUPPRESS_MS;
};

export const shouldIgnoreChecklistStorageEvent = () => Date.now() < checklistStorageWriteSuppressUntil;

const dispatchChecklistProgressNow = (companyId) => {
    const detail = { companyId: companyId || null };
    try {
        window.dispatchEvent(new CustomEvent(CHECKLIST_PROGRESS_EVENT, { detail }));
    } catch (e) {
        // no-op
    }
    try {
        if (typeof BroadcastChannel !== 'undefined') {
            const channel = new BroadcastChannel(CHECKLIST_PROGRESS_BROADCAST_CHANNEL);
            channel.postMessage(detail);
            channel.close();
        }
    } catch (e) {
        // no-op
    }
    try {
        const payload = { type: CHECKLIST_PROGRESS_EVENT, ...detail };
        const origin = window.location?.origin || '*';
        if (window.parent && window.parent !== window) {
            window.parent.postMessage(payload, origin);
        }
        if (typeof document !== 'undefined') {
            document.querySelectorAll('iframe').forEach((frame) => {
                try {
                    frame.contentWindow?.postMessage(payload, origin);
                } catch (e) {
                    // no-op
                }
            });
        }
    } catch (e) {
        // no-op
    }
};

/**
 * Notify checklist UIs to reload (debounced — avoids broadcast/storage feedback loops).
 */
export const notifyChecklistProgressUpdated = (companyId) => {
    if (checklistNotifyTimer) {
        clearTimeout(checklistNotifyTimer);
    }
    checklistNotifyTimer = setTimeout(() => {
        checklistNotifyTimer = null;
        dispatchChecklistProgressNow(companyId);
    }, 120);
};

/** DOM event: dashboard host requests checklist popup auto-open if conditions allow. */
export const CHECKLIST_AUTO_OPEN_REQUEST_EVENT = 'onboarding-checklist-auto-open-request';

/** Request checklist popup auto-open (handled by AppChatbot when dashboard loads). */
export const requestChecklistAutoOpen = () => {
    try {
        window.dispatchEvent(new CustomEvent(CHECKLIST_AUTO_OPEN_REQUEST_EVENT));
    } catch (e) {
        // no-op
    }
};

/** Query key for default DynamicContent left-menu tab when opened from checklist. */
export const CHECKLIST_DYNAMIC_CONTENT_TAB_QUERY = 'checklistTab';

/** EditDynamicContent.vue selectMenuValue keys */
export const DYNAMIC_CONTENT_CHECKLIST_TABS = {
    HOME_PAGE: 'HomePageContent',
    FOOTER: 'FooterContent',
    LOGO: 'Logo'
};

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const toLower = (value) => String(value || '').trim().toLowerCase();

export const isValidGuid = (value) => GUID_REGEX.test(String(value || '').trim());

export const parseGuidOrNull = (value) => (isValidGuid(value) ? String(value).trim() : null);

export const getApiStatusCode = (payload) => {
    if (!payload || typeof payload !== 'object') {
        return null;
    }
    const code = payload.statusCode ?? payload.StatusCode;
    if (code === undefined || code === null) {
        return null;
    }
    return Number(code);
};

/** Resolve company id from localStorage and userinfo payload. */
export const resolveCompanyId = () => {
    const fromStorage = parseGuidOrNull(localStorage.getItem('companyId'));
    if (fromStorage && fromStorage !== EMPTY_GUID) {
        return fromStorage;
    }
    try {
        const raw = localStorage.getItem('userinfo');
        if (!raw) {
            return fromStorage;
        }
        const parsed = JSON.parse(raw);
        const fromUser = parseGuidOrNull(parsed?.data?.companyId) || parseGuidOrNull(parsed?.companyId);
        if (fromUser && fromUser !== EMPTY_GUID) {
            return fromUser;
        }
    } catch (e) {
        // no-op
    }
    return fromStorage;
};

export const getChecklistContext = () => {
    try {
        const params = new URLSearchParams(window?.location?.search || '');
        const stepIdFromQuery = params.get('stepId');
        const sourceFromQuery = params.get('source');
        const companyId = resolveCompanyId() || '';

        if (toLower(sourceFromQuery) === 'checklist' && stepIdFromQuery) {
            return {
                stepId: String(stepIdFromQuery),
                source: 'checklist',
                companyId
            };
        }

        const raw = localStorage.getItem(CHECKLIST_PENDING_STEP_STORAGE_KEY);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw);
        if (!parsed?.stepId) {
            return null;
        }

        return {
            stepId: String(parsed.stepId),
            source: toLower(parsed.source) || 'checklist',
            companyId: parseGuidOrNull(parsed.companyId) || companyId || ''
        };
    } catch (e) {
        return null;
    }
};

const refreshChecklistContextCompanyId = (saveBody) => {
    if (!saveBody || !isChecklistFlowActive()) {
        return;
    }
    const companyId = parseGuidOrNull(saveBody.companyId || saveBody.CompanyId);
    if (!companyId) {
        return;
    }
    const context = getChecklistContext();
    if (!context?.stepId) {
        return;
    }
    localStorage.setItem(CHECKLIST_PENDING_STEP_STORAGE_KEY, JSON.stringify({
        companyId,
        stepId: context.stepId,
        source: 'checklist',
        updatedAt: new Date().toISOString()
    }));
};

export const withChecklistCompletion = (config = {}) => ({
    ...(config || {}),
    completeChecklistStep: true
});

export const syncChecklistContextFromRoute = (route) => {
    try {
        const query = route?.query || {};
        const source = String(query.source || '').trim().toLowerCase();
        const stepId = query.stepId;
        if (source !== 'checklist' || !stepId) {
            return;
        }
        const companyId = resolveCompanyId() || '';
        localStorage.setItem(CHECKLIST_PENDING_STEP_STORAGE_KEY, JSON.stringify({
            companyId,
            stepId: String(stepId),
            source: 'checklist',
            updatedAt: new Date().toISOString()
        }));
    } catch (e) {
        // no-op
    }
};

export const isChecklistFlowActive = () => {
    try {
        const params = new URLSearchParams(window?.location?.search || '');
        const source = String(params.get('source') || '').trim().toLowerCase();
        if (source === 'checklist' && params.get('stepId')) {
            return true;
        }
        const raw = localStorage.getItem(CHECKLIST_PENDING_STEP_STORAGE_KEY);
        if (!raw) {
            return false;
        }
        const parsed = JSON.parse(raw);
        return Boolean(parsed?.stepId);
    } catch (e) {
        return false;
    }
};

export const isChecklistSaveSuccess = (response) => {
    const httpStatus = Number(response?.status || 0);
    if (httpStatus < 200 || httpStatus >= 300) {
        return false;
    }
    const payload = response?.data;
    if (!payload || typeof payload !== 'object') {
        return true;
    }
    const statusCode = getApiStatusCode(payload);
    if (statusCode === null) {
        return true;
    }
    if (statusCode !== 200) {
        return false;
    }
    const result = payload.result ?? payload.Result;
    if (result === 2 || result === '2' || result === false || result === 0 || result === '0') {
        return false;
    }
    return true;
};

/** Always call Onboarding/UpdateStepStatus after a successful checklist module save. */
export const triggerChecklistStepUpdateAfterSave = async (response, saveBody = null) => {
    if (!isChecklistSaveSuccess(response) || !isChecklistFlowActive()) {
        return false;
    }
    if (saveBody) {
        refreshChecklistContextCompanyId(saveBody);
    }
    const onboardingService = (await import('@/service/onboarding.service')).default;
    return onboardingService.completePendingChecklistStep();
};

const triggerVendorChecklistSyncAfterSave = async (response, saveBody = null) => {
    if (!isChecklistSaveSuccess(response)) {
        return false;
    }
    const companyId = parseGuidOrNull(saveBody?.companyId || saveBody?.CompanyId) || resolveCompanyId();
    if (!companyId) {
        return false;
    }
    const onboardingService = (await import('@/service/onboarding.service')).default;
    const result = await onboardingService.syncVendorChecklistStep(companyId);
    return Boolean(result?.updated);
};

export const postWithChecklistCompletion = async (apiInstance, url, data, config = {}) => {
    const response = await apiInstance.post(url, data, config);
    await triggerChecklistStepUpdateAfterSave(response, data);
    const urlLower = String(url || '').toLowerCase();
    if (urlLower.includes('managevendor/registervendor')) {
        await triggerVendorChecklistSyncAfterSave(response, data);
    }
    return response;
};
